/* eslint-disable complexity */
import {
  ROUTES,
  extractJsonFromBase64,
  ApiClientType,
  APIClients,
  IntegrationsAPIClient,
  auth0Authenticator,
  isAuth0Authenticated,
  getPaymentDetailsPageUrl,
  getWorkspaceDetailSettingsUrl,
  QUERY_PARAMS,
  getProductUrl,
  getOrganisationDetailUrl,
  getOrganisationsUrl
} from '@futrli/shared-services';
import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';

import { LOGIN_ROUTE } from '../../core/constants';
import { resetError } from '../../core/redux-utils/actions';
import { StoreState } from '../../core/types';
import { storeInvitationData } from '../../utils/invite';
import { COMPLETE_NEW_PASSWORD, SIGN_IN_INVITATION } from '../constants';
import { UserInvitationData } from '../types';
import { checkIfAuthenticated } from '../../auth/service';
import { trackEvent } from '../../../../predict/src/core/tracker';
import { TRACKING_EVENTS } from '../../../../predict/src/core/constants';
import { SageInviteForm } from './sage-invite-form';

/**
 * Get the landing page url.
 * - If the invitation is coming from sales portal, we redirect to payment details page.
 * - There are no organisations assigned -> workspace settings
 * - One organisation assigned and product undefined or your_actionfeed -> your actionfeed.
 * - One organisation assigned and product defined -> product.
 * - More than one org assigned -> list of organisations.
 *
 * @returns {string} landing url.
 */
export const getLandingUrl = (userInvitationData: UserInvitationData): string => {
  const { organisationId, organisationCount, product, workspaceId, viaSalesPortal } = userInvitationData;

  if (viaSalesPortal) {
    return `${getPaymentDetailsPageUrl(workspaceId)}?${QUERY_PARAMS.ORG_ID}=${organisationId}`;
  }

  if (!organisationCount) {
    return getWorkspaceDetailSettingsUrl(workspaceId);
  }

  if (organisationId) {
    return product
      ? getProductUrl(workspaceId, organisationId, product)
      : getOrganisationDetailUrl(workspaceId, organisationId);
  }

  return getOrganisationsUrl(workspaceId);
};

/**
 * Invitation page.
 */
export const SageInvite = () => {
  const [isRedirecting, setIsRedirecting] = useState(false);
  const { user: tempUser } = useParams();
  const userDataBase64 = tempUser || '';
  const userInvitationData: UserInvitationData = extractJsonFromBase64(userDataBase64);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const isAuthenticated = useSelector((state: StoreState) => checkIfAuthenticated(state.authReducers));

  /**
   * Get the initial values to fill the invite form
   * @param {AuthenticatorUser} user - Signed in user.
   * @returns {Partial<RegisterFormData>}
   */
  const initialFormValues = useMemo(() => {
    if (!userInvitationData) {
      return {};
    }

    const { userName } = userInvitationData;

    return {
      username: userName
    };
  }, [userInvitationData]);

  const handleRedirect = useCallback(
    (email: string) => {
      if (!userInvitationData) {
        return;
      }
      setIsRedirecting(true);
      APIClients.setApiClient(ApiClientType.Auth0);
      IntegrationsAPIClient.setApiClient(ApiClientType.Auth0);
      auth0Authenticator.signIn(email);
    },
    [userInvitationData]
  );

  const handleNewSubmit = (values: { username: string }) => {
    trackEvent(TRACKING_EVENTS.ACCEPTED_INVITE);
    handleRedirect(values.username);
  };

  React.useEffect(() => {
    if (isAuth0Authenticated()) {
      IntegrationsAPIClient.setApiClient(ApiClientType.Auth0);
      APIClients.setApiClient(ApiClientType.Auth0);
    } else {
      IntegrationsAPIClient.setApiClient(ApiClientType.Aws);
      APIClients.setApiClient(ApiClientType.Aws);
    }
    const landingPath = getLandingUrl(userInvitationData);
    // Checks if the user is authenticated and redirects into the app.
    if (isAuthenticated) {
      navigate(ROUTES.ROOT_ROUTE);
      return;
    }

    // If the user is already a futrli user (internal user) and is unauthenticated, will be redirected to login.
    if (!userInvitationData.created) {
      navigate(LOGIN_ROUTE);
      return;
    }

    const { viaSalesPortal } = userInvitationData;
    // We need to save data to session storage for provision request.
    storeInvitationData(
      landingPath,
      userInvitationData.workspaceId,
      userInvitationData.invitationReference,
      viaSalesPortal ? 'sales' : 'predict',
      userInvitationData.business_type
    );

    return () => {
      dispatch(resetError(SIGN_IN_INVITATION));
      dispatch(resetError(COMPLETE_NEW_PASSWORD));
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <SageInviteForm
      invitedUser={true}
      initialEmail={initialFormValues.username}
      isLoading={isRedirecting}
      // hideForm={Boolean(signInError)}
      // cognitoError={signInError || completeNewPasswordError}
      onSubmit={handleNewSubmit}
    />
  );
};
